import { PropsWithChildren, useEffect, useState } from "react";
import { Navigate } from "@tanstack/react-router";
import { fetchAuthSession, signInWithRedirect } from "aws-amplify/auth";
import { initAmplify } from "./amplify";

export function AuthGuard({ children }: PropsWithChildren) {
  initAmplify();
  const [loading, setLoading] = useState(true);
  const [isAuthed, setIsAuthed] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const session = await fetchAuthSession();
        const authed = Boolean(session.tokens?.idToken);
        if (mounted) setIsAuthed(authed);
      } catch {
        if (mounted) setIsAuthed(false);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  if (loading) return null;
  if (!isAuthed) signInWithRedirect({ options: { lang: "ja" } });
  return <>{children}</>;
}

type RoleGuardProps = PropsWithChildren & {
  allowedGroups?: string[];
};

export function RoleGuard({ children, allowedGroups }: RoleGuardProps) {
  initAmplify();
  const [loading, setLoading] = useState(true);
  const [isAuthed, setIsAuthed] = useState(false);
  const [groups, setGroups] = useState<string[]>([]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const session = await fetchAuthSession();
        const authed = Boolean(session.tokens?.idToken);
        const idPayload = session.tokens?.idToken?.payload as
          | Record<string, unknown>
          | undefined;
        const atPayload = session.tokens?.accessToken?.payload as
          | Record<string, unknown>
          | undefined;
        let gs = (idPayload?.["cognito:groups"] ??
          atPayload?.["cognito:groups"]) as string | string[] | undefined;
        const normGroups = Array.isArray(gs)
          ? (gs as string[])
          : gs
            ? [String(gs)]
            : [];
        if (mounted) {
          setIsAuthed(authed);
          setGroups(normGroups);
        }
      } catch {
        if (mounted) {
          setIsAuthed(false);
          setGroups([]);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  if (loading) return null;
  if (!isAuthed) {
    signInWithRedirect({ options: { lang: "ja" } });
    return null;
  }

  const isAllowed = !allowedGroups?.length
    ? true
    : groups.some((g) => allowedGroups.includes(g));
  if (!isAllowed) {
    return <Navigate to="/forbidden" />;
  }

  return <>{children}</>;
}

type RoleRedirectProps = PropsWithChildren;

export function RoleRedirect({ children }: RoleRedirectProps) {
  initAmplify();
  const [loading, setLoading] = useState(true);
  const [isAuthed, setIsAuthed] = useState(false);
  const [groups, setGroups] = useState<string[]>([]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const session = await fetchAuthSession();
        const authed = Boolean(session.tokens?.idToken);
        const idPayload = session.tokens?.idToken?.payload as
          | Record<string, unknown>
          | undefined;
        const atPayload = session.tokens?.accessToken?.payload as
          | Record<string, unknown>
          | undefined;
        let gs = (idPayload?.["cognito:groups"] ??
          atPayload?.["cognito:groups"]) as string | string[] | undefined;
        const normGroups = Array.isArray(gs)
          ? (gs as string[])
          : gs
            ? [String(gs)]
            : [];
        if (mounted) {
          setIsAuthed(authed);
          setGroups(normGroups);
        }
      } catch {
        if (mounted) {
          setIsAuthed(false);
          setGroups([]);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  if (loading) return null;
  if (!isAuthed) return <>{children}</>;

  const hasAny = (cands: string[]) => groups.some((g) => cands.includes(g));
  const to = hasAny(["ops-admin", "ops-operator"])
    ? "/bizsol/dashboard"
    : hasAny(["clinic-doctor", "clinic-operator"])
      ? "/clinic/dashboard"
      : hasAny(["patient-admin", "health-nurse"])
        ? "/company-admin/dashboard"
        : hasAny(["patient-user"])
          ? "/patient/home"
          : null;

  if (to) return <Navigate to={to} />;
  return <>{children}</>;
}
